import {
  ApartmentOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  LogoutOutlined,
  RightOutlined,
  SettingOutlined,
  SwapOutlined,
  TableOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Badge, Button, Card, List, Typography } from 'antd';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { BusinessSelector } from '../components/BusinessSelector';
import { usePendingApprovals } from '../hooks/usePendingApprovalsCount';
import { usePendingShipmentsCount } from '../hooks/usePendingShipmentsCount';

/** /more hub (mobile navigation overflow): shipments, approvals, reports,
 * settings, sign-out. */
export function MorePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isPlatformAdmin } = useAuth();
  const pendingShipments = usePendingShipmentsCount();
  const { total: pendingApprovals } = usePendingApprovals();

  const links = [
    {
      key: '/shipments',
      icon: <FileDoneOutlined />,
      label: t('nav.shipments'),
      badge: pendingShipments,
    },
    {
      key: '/approvals',
      icon: <CheckCircleOutlined />,
      label: t('nav.approvals'),
      badge: pendingApprovals,
    },
    { key: '/movements', icon: <SwapOutlined />, label: t('nav.movements'), badge: 0 },
    { key: '/reports', icon: <FileTextOutlined />, label: t('nav.reports'), badge: 0 },
    { key: '/products', icon: <BarChartOutlined />, label: t('nav.products'), badge: 0 },
    { key: '/summary', icon: <TableOutlined />, label: t('nav.summary'), badge: 0 },
    { key: '/settings', icon: <SettingOutlined />, label: t('nav.settings'), badge: 0 },
    ...(isPlatformAdmin
      ? [{ key: '/businesses', icon: <ApartmentOutlined />, label: t('nav.businesses'), badge: 0 }]
      : []),
  ];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Card size="small">
        <Card.Meta
          avatar={<Avatar size={44} icon={<UserOutlined />} />}
          title={user?.name}
          description={user?.login}
        />
        {isPlatformAdmin ? (
          <div style={{ marginTop: 12 }}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
              {t('common.business')}
            </Typography.Text>
            <BusinessSelector style={{ width: '100%', maxWidth: '100%' }} />
          </div>
        ) : null}
      </Card>
      <Card size="small" styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}>
        <List
          dataSource={links}
          renderItem={(link) => (
            <List.Item
              onClick={() => navigate(link.key)}
              style={{ cursor: 'pointer', paddingInline: 4 }}
              extra={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  {link.badge > 0 ? <Badge count={link.badge} size="small" /> : null}
                  <RightOutlined style={{ color: '#bbb' }} />
                </span>
              }
            >
              <Typography.Text style={{ fontSize: 16 }}>
                <span style={{ marginInlineEnd: 10 }}>{link.icon}</span>
                {link.label}
              </Typography.Text>
            </List.Item>
          )}
        />
      </Card>
      <Button icon={<LogoutOutlined />} size="large" onClick={() => void logout()} danger block>
        {t('common.logout')}
      </Button>
    </div>
  );
}
